/* eslint-disable @typescript-eslint/no-floating-promises */

import 'styled-components/macro'
import { forwardRef, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useCategoryManager } from '@velasca/hooks/use-category-manager'
import type {
  CollectionBannerProps,
  CollectionMessageData,
  NullableMetafield,
} from '@velasca/types'
import { getSquaredBannerImageFileName } from '@velasca/utils/get-squared-banner-image-file-name'
import { $settings } from '@velasca/utils/globals'
import { handleError } from '@velasca/utils/handle-error'
import { ApparelCollectionPresentational } from './ApparelCollectionPresentational'
import type { ApparelCollectionCommonProps } from './types'
import useCategory from './useCategory'
import { formatApparelData, groupByProduct } from './utils'

interface MakeBannerData {
  collection: {
    title: string
    description: string
    squaredBannerImage?: NullableMetafield & {
      reference?: {
        image?: {
          url?: string
        }
      }
    }
    handle: string
  }
}

const makeBanner = (data: MakeBannerData, alt = ''): CollectionBannerProps => {
  return {
    title: data?.collection?.title,
    description: data?.collection?.description,
    img: {
      src:
        data?.collection?.squaredBannerImage?.reference?.image?.url ??
        `${$settings.imageAssetPublicPath}${getSquaredBannerImageFileName(
          data?.collection?.handle,
        )}`,
      alt,
    },
    collectionUrl: data?.collection?.handle
      ? `/collections/${data?.collection?.handle}`
      : '',
  }
}

interface ApparelCollectionBaseProps extends ApparelCollectionCommonProps {
  skip?: boolean
  groupProducts?: boolean
  showCollectionBannerOnMobile?: boolean
  productsCount?: number
}

// Required to make either categoryID or categoryHandle required
export interface ApparelCollectionPropsWithHandle
  extends ApparelCollectionBaseProps {
  categoryHandle: string
  categoryID?: never
  collectionTopMessageData?: CollectionMessageData
  productsCount?: number
}

interface ApparelCollectionPropsWithID extends ApparelCollectionBaseProps {
  categoryID: string
  categoryHandle?: never
  collectionTopMessageData?: CollectionMessageData
}

type ApparelCollectionProps =
  | ApparelCollectionPropsWithHandle
  | ApparelCollectionPropsWithID

const ApparelCollection = forwardRef<HTMLDivElement, ApparelCollectionProps>(
  (
    {
      categoryHandle,
      categoryID,
      showBanner,
      showSingleVariant,
      skeletonLength,
      productsCount = 0,
      skip = false,
      showProductType,
      showReviewBadge,
      groupProducts = false,
      className = '',
      shouldLoadAllProducts = false,
      collectionAltStringTemplate,
      collectionTopMessageData,
      showCollectionBannerOnMobile,
    }: ApparelCollectionProps,
    ref,
  ) => {
    const { t } = useTranslation('product')

    useCategoryManager()

    const { loading, data, fetchMore, error } = useCategory({
      variables: {
        handle: categoryHandle,
        id: categoryID,
        first: shouldLoadAllProducts ? 250 : 50,
      },
      skip,
      notifyOnNetworkStatusChange: true,
    })

    const banner = useMemo(
      () =>
        data &&
        makeBanner(
          data as any,
          t(collectionAltStringTemplate ?? 'Apparel Banner', {
            title: data?.collection?.title,
          }),
        ),
      [collectionAltStringTemplate, data, t],
    )

    const products = useMemo(
      () =>
        data &&
        (groupProducts
          ? groupByProduct(data.collection)
          : formatApparelData(data.collection)),
      [data, groupProducts],
    )

    useEffect(() => {
      if (!error) {
        return
      }

      handleError(error, { behaviour: 'throw' })
    }, [error])

    useEffect(() => {
      if (data?.collection === null) {
        handleError(new Error(`collection ${categoryHandle ?? ''} is null`))
      }
    }, [categoryHandle, data])

    const after = data?.collection?.products?.pageInfo?.endCursor

    const handleLoadMore = useCallback(() => {
      fetchMore({
        variables: {
          after,
        },
      })
    }, [fetchMore, after])

    return (
      <ApparelCollectionPresentational
        shouldLoadAllProducts={shouldLoadAllProducts}
        ref={ref}
        loading={loading || skip}
        showBanner={showBanner}
        showSingleVariant={showSingleVariant}
        skeletonLength={skeletonLength}
        showProductType={showProductType}
        showReviewBadge={showReviewBadge}
        className={className}
        productsCount={productsCount}
        canLoadMore={data?.collection?.products?.pageInfo?.hasNextPage}
        collectionTopMessageData={collectionTopMessageData}
        showCollectionBannerOnMobile={showCollectionBannerOnMobile}
        products={products}
        banner={banner}
        onLoadMore={handleLoadMore}
      />
    )
  },
)

export default ApparelCollection
